import {
  checkFixLink,
  checkMetadataOf,
  CheckOutcome,
  controlStatusLabel,
  CheckStandard,
  compareCategories,
  controlCheckFields,
  DeclaredFact,
  DeclaredRetriever,
  enforcementLabel,
  EnforcementLevel,
  FactRows,
  factSourceHints,
  failingFactIds,
  FixLink,
  formatFactValue,
  FrontDoorCheckMetadata,
  indexDeclaredFacts,
  isEnforcedByGitHub,
  joinChecksAndResults,
  latestFactValue,
  notApplicableHint,
  notApplicableLabel,
  OUTCOME_ORDER,
  plural,
  SCORECARD_CATEGORY_IDS,
  ScorecardCategory,
  standardOf,
  StandardSummary,
  tally,
  TechInsightsCheck,
  TechInsightsCheckResult,
  TechInsightsFact,
  unmeasuredControls,
} from "@internal/plugin-front-door-common";

/**
 * Pure helpers behind the service Scorecard tab: group Tech Insights checks by
 * category, sort failures first, detect checks with no data, and turn the facts
 * behind a failure into short human text. What each check and fact means comes
 * from the check metadata and the declared facts (`retrievers`).
 */

export type { FactRows, FixLink };

export type RowStatus = CheckOutcome;

export interface CheckRow {
  id: string;
  name: string;
  description: string;
  /** How the check is measured, shown under the description. */
  measurement?: string;
  status: RowStatus;
  enforcement: EnforcementLevel;
  /** Badge text: the enforcement level's label. */
  enforcementLabel: string;
  /** GitHub already enforces the check (metadata `enforcedBy: github`). */
  enforcedByGitHub: boolean;
  standard?: CheckStandard;
  /** "Proposed" and so on while the control is not active. */
  statusLabel?: string;
  /** The control's general fix guidance. */
  howToFix?: string;
  /** How to make the fix in Backstage's tools, after `howToFix`. */
  fixSteps?: string;
  fix?: FixLink;
  /** Formatted values that made the check fail (failing rows only). */
  evidence: string[];
  /** Formatted facts shown beside a passing check. */
  detail?: string[];
  /** Why there is no result (no-data rows only). */
  noDataHint?: string;
  /** The check does not apply to this service; it never scores. */
  notApplicable?: boolean;
  /** Chip text of a not-applicable row, e.g. "Not scanned". */
  notApplicableLabel?: string;
}

/** A registry control in the section's category that no check measures. */
export interface NotAutomatedRow {
  id: string;
  name: string;
  statement: string;
  howToFix: string;
  statusLabel?: string;
  fix?: FixLink;
}

export interface CategorySection {
  category: ScorecardCategory;
  rows: CheckRow[];
  /** Self-attested, not automated: never counted in the tallies. */
  notAutomated: NotAutomatedRow[];
  passed: number;
  failed: number;
  noData: number;
  /** Checks with a result (passed + failed). */
  scored: number;
  /** Short reason when every check in the section has no data. */
  noDataSummary?: string;
}

export interface ServiceScorecardModel {
  sections: CategorySection[];
  passed: number;
  failed: number;
  noData: number;
  scored: number;
  /** Newest fact row timestamp across the sources the checks use. */
  lastUpdated?: Date;
  /** Per-source timestamps, for a tooltip. */
  sourceUpdated: Array<{ source: string; at: Date }>;
}

/** The declared facts as the model reads them; absent when they could not be loaded. */
export type Retrievers = ReadonlyArray<DeclaredRetriever> | undefined;

// ---------------------------------------------------------------------------
// No-data hints

const metadataOf = (check: TechInsightsCheck) =>
  checkMetadataOf(check.metadata);

/** Why a check returned by getAllChecks has no result for this entity. */
export function noDataHintFor(
  check: TechInsightsCheck,
  rows?: FactRows,
  retrievers?: Retrievers
): string {
  const notApplicable = notApplicableHint(metadataOf(check), (fact) =>
    latestFactValue(check, rows, fact)
  );
  if (notApplicable) return notApplicable;
  const retrieverId = check.factIds?.[0];
  const hints = factSourceHints(retrievers, retrieverId ?? "");
  if (!retrieverId || !rows) return hints.noRow;
  return rows[retrieverId] ? hints.missingFact : hints.noRow;
}

// ---------------------------------------------------------------------------
// Fact formatting

/** 0 → "today", 1 → "1 day", n → "n days". */
export function days(n: number): string {
  return n === 0 ? "today" : plural(n, "day");
}

/** "daysSinceLastSuccessfulDeploy" → "days since last successful deploy". */
export function humanizeFactId(id: string): string {
  return id
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLocaleLowerCase("en-US")
    .trim();
}

function formatGeneric(
  id: string,
  value: unknown,
  description?: string
): string {
  const label = description?.trim() || humanizeFactId(id);
  if (typeof value === "boolean") return `${label}: ${value ? "yes" : "no"}`;
  if (typeof value === "number") {
    if (/days?/i.test(id)) return `${label}: ${days(value)}`;
    return `${label}: ${value.toLocaleString("en-US")}`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return `${label}: none`;
    const shown = value.slice(0, 3).map(String).join(", ");
    return `${label}: ${shown}${
      value.length > 3 ? ` +${value.length - 3} more` : ""
    }`;
  }
  if (value === null || value === undefined || value === "")
    return `${label}: unknown`;
  if (typeof value === "object") return `${label}: ${JSON.stringify(value)}`;
  return `${label}: ${String(value)}`;
}

/**
 * One fact as short human text: its declared format, else its description
 * (declared or from the check result) and value.
 */
export function formatFact(
  id: string,
  value: unknown,
  declared?: Partial<DeclaredFact> & Partial<TechInsightsFact>
): string {
  const formatted = declared?.format
    ? formatFactValue(declared.format, value)
    : undefined;
  return formatted ?? formatGeneric(id, value, declared?.description);
}

/** Formats facts by name with what their retrievers declare. */
function factFormatter(retrievers: Retrievers) {
  const declared = indexDeclaredFacts(retrievers ?? []);
  return (id: string, value: unknown, fact?: TechInsightsFact) =>
    formatFact(id, value, { ...fact, ...declared.get(id) });
}

/** Human text for the values behind a failing check. */
export function evidenceFor(
  result: TechInsightsCheckResult,
  rows?: FactRows,
  retrievers?: Retrievers
): string[] {
  const format = factFormatter(retrievers);
  const metadata: FrontDoorCheckMetadata = metadataOf(result.check);
  const failing = failingFactIds(result);
  const contextValues: Array<[string, unknown]> = (metadata.contextFacts ?? [])
    .map((factId): [string, unknown] => [
      factId,
      latestFactValue(result.check, rows, factId),
    ])
    .filter(([, value]) => value !== undefined);
  const redundant =
    contextValues.length > 0 ? metadata.redundantWithContext ?? [] : [];
  const facts = result.facts ?? {};
  const ruleText = failing
    .filter((factId) => !redundant.includes(factId))
    .map((factId) => format(factId, facts[factId]?.value, facts[factId]));
  const contextText = contextValues
    .filter(([factId]) => !(factId in facts))
    .map(([factId, value]) => format(factId, value));
  return [...ruleText, ...contextText];
}

/** Text for the passingDetailFacts of a passing check, from the result then the fact rows. */
export function detailFor(
  result: TechInsightsCheckResult,
  rows?: FactRows,
  retrievers?: Retrievers
): string[] | undefined {
  const ids = metadataOf(result.check).passingDetailFacts;
  if (!ids) return undefined;
  const format = factFormatter(retrievers);
  return ids
    .map((factId): [string, unknown] => [
      factId,
      result.facts?.[factId]?.value ??
        latestFactValue(result.check, rows, factId),
    ])
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([factId, value]) => format(factId, value));
}

// ---------------------------------------------------------------------------
// Rows, sections, model

export function buildModel(input: {
  checks: TechInsightsCheck[];
  results: TechInsightsCheckResult[];
  factRows?: FactRows;
  githubSlug?: string;
  /** From the blackboard-standards registry, by id. */
  standards?: ReadonlyMap<string, StandardSummary>;
  /** The declared facts: formats and no-data hints. */
  retrievers?: Retrievers;
}): ServiceScorecardModel {
  const { checks, results, factRows, githubSlug, standards, retrievers } =
    input;
  const joined = joinChecksAndResults(checks, results);

  const rows = new Map<ScorecardCategory, CheckRow[]>();
  for (const { definition, effective, result, outcome } of joined) {
    const metadata = metadataOf(effective);
    const notApplicable =
      notApplicableHint(metadata, (fact) =>
        latestFactValue(definition, factRows, fact)
      ) !== undefined;
    // Only a boolean result scores; null or missing is no data.
    const status = notApplicable ? "noData" : outcome;
    const standard = standardOf(metadata.standardId, standards);
    const row: CheckRow = {
      id: definition.id,
      name: effective.name || definition.id,
      description: effective.description ?? "",
      measurement: metadata.measurement,
      status,
      enforcement: metadata.enforcement,
      enforcementLabel: enforcementLabel(metadata),
      enforcedByGitHub: isEnforcedByGitHub(metadata),
      standard,
      statusLabel: controlStatusLabel(metadata),
      howToFix: metadata.howToFix,
      fixSteps: metadata.fixSteps,
      fix: checkFixLink(effective, { githubSlug, standard }),
      evidence:
        status === "fail" && result
          ? evidenceFor(result, factRows, retrievers)
          : [],
      detail:
        status === "pass" && result
          ? detailFor(result, factRows, retrievers)
          : undefined,
      noDataHint:
        status === "noData"
          ? noDataHintFor(definition, factRows, retrievers)
          : undefined,
      notApplicable: notApplicable || undefined,
      notApplicableLabel: notApplicable
        ? notApplicableLabel(metadata)
        : undefined,
    };
    rows.set(metadata.category, [...(rows.get(metadata.category) ?? []), row]);
  }

  const notAutomated = new Map<ScorecardCategory, NotAutomatedRow[]>();
  for (const ref of unmeasuredControls(
    [...(standards?.values() ?? [])],
    checks.map((c) => c.id)
  )) {
    const fields = controlCheckFields(ref);
    const row: NotAutomatedRow = {
      id: ref.control.id,
      name: fields.name,
      statement: fields.description,
      howToFix: fields.metadata.howToFix,
      statusLabel: controlStatusLabel(fields.metadata),
      fix: checkFixLink(
        { metadata: fields.metadata },
        { standard: standardOf(ref.standard.id, standards) }
      ),
    };
    const category = fields.metadata.category;
    notAutomated.set(category, [...(notAutomated.get(category) ?? []), row]);
  }

  const sections: CategorySection[] = [];
  for (const category of SCORECARD_CATEGORY_IDS) {
    const categoryRows = rows.get(category) ?? [];
    const unmeasured = notAutomated.get(category) ?? [];
    if (!categoryRows.length && !unmeasured.length) continue;
    categoryRows.sort(
      (a, b) =>
        OUTCOME_ORDER[a.status] - OUTCOME_ORDER[b.status] ||
        a.name.localeCompare(b.name)
    );
    const counts = tally(categoryRows.map((r) => r.status));
    const section: CategorySection = {
      category,
      rows: categoryRows,
      notAutomated: unmeasured,
      passed: counts.passed,
      failed: counts.failed,
      noData: counts.noData,
      scored: counts.total,
    };
    if (
      categoryRows.length > 0 &&
      categoryRows.every((r) => r.status === "noData")
    ) {
      const sourceIds = Array.from(
        new Set(
          joined
            .map((j) => j.definition)
            .filter((c) => categoryRows.some((r) => r.id === c.id))
            .map((c) => c.factIds?.[0])
            .filter((id): id is string => Boolean(id))
        )
      );
      section.noDataSummary = Array.from(
        new Set(
          sourceIds.map((id) => factSourceHints(retrievers, id).noDataShort)
        )
      ).join("; ");
    }
    sections.push(section);
  }

  // Failures first, then fully passing, then sections with no data at all; fixed order within.
  const rank = (s: CategorySection) => {
    if (s.failed > 0) return 0;
    return s.scored > 0 ? 1 : 2;
  };
  sections.sort(
    (a, b) => rank(a) - rank(b) || compareCategories(a.category, b.category)
  );

  const sourceUpdated = Object.entries(factRows ?? {})
    .map(([retrieverId, row]) => {
      const at = row?.timestamp ? new Date(row.timestamp) : undefined;
      return at && !isNaN(at.getTime())
        ? { source: factSourceHints(retrievers, retrieverId).label, at }
        : undefined;
    })
    .filter((x): x is { source: string; at: Date } => !!x)
    .sort((a, b) => b.at.getTime() - a.at.getTime());

  const sum = (key: "passed" | "failed" | "noData") =>
    sections.reduce((acc, s) => acc + s[key], 0);
  const passed = sum("passed");
  const failed = sum("failed");
  return {
    sections,
    passed,
    failed,
    noData: sum("noData"),
    scored: passed + failed,
    lastUpdated: sourceUpdated[0]?.at,
    sourceUpdated,
  };
}

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export function relativeTime(at: Date, now: Date = new Date()): string {
  const seconds = Math.max(
    0,
    Math.round((now.getTime() - at.getTime()) / 1000)
  );
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${plural(minutes, "minute")} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${plural(hours, "hour")} ago`;
  return `${plural(Math.round(hours / 24), "day")} ago`;
}
